import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowsAlt,
  FaChair,
  FaCompress,
  FaExpand,
  FaMousePointer,
  FaPlus,
  FaSave,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import firebase from "firebase";
import "firebase/firestore";
import { SeatMapCanvas } from "@alisaitteke/seatmap-canvas";
import "@alisaitteke/seatmap-canvas/dist/seatmap.canvas.css";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";

const SEAT_SPACING = 34;
const DEFAULT_ROWS = 10;
const DEFAULT_COLUMNS = 12;
const HOLD_LAYOUT_EVENT_ID = "1000905258664";
const HOLD_LAYOUT_PRESET = "holds-1000905258664";
const HOLD_STAGE_BALCONY_PRESET = "holds-stage-balcony-1000905258664";
const FOUR_TYPE_ORCHESTRA_EVENT_ID = "1000110518720";
const FOUR_TYPE_ORCHESTRA_PRESET = "four-type-orchestra-1000110518720";
const COMBINED_FULL_VENUE_PRESET = "combined-full-venue-1000110518720";
const SIX_TIER_FULL_VENUE_EVENT_ID = "1000664709217";
const SIX_TIER_FULL_VENUE_PRESET = "six-tier-full-venue-1000664709217";
const FOUR_TYPE_ORCHESTRA_COLORS = ["#0047d9", "#f28c28", "#d71920", "#6b21a8"];
const COMBINED_FULL_VENUE_COLORS = ["#0047d9", "#f28c28", "#d71920", "#6b21a8", "#0f8b96"];
const SIX_TIER_COLORS = {
  1: "#1238d8",
  2: "#f8bd83",
  3: "#f28c28",
  4: "#d71920",
  5: "#6b21a8",
  6: "#0f8b96",
};
const HOLD_OPEN_COLOR = "#050505";
const SEAT_SHAPE_SELECTOR = ".seat-circle, .seat-rect, .seat-path, .seat-custom, circle, rect, path";

const statusOptions = [
  { value: "pending", label: "Pending", color: "#22c55e" },
  { value: "reserved", label: "Reserved", color: "#f59e0b" },
  { value: "sold", label: "Sold", color: "#ef4444" },
  { value: "disabled", label: "Disabled", color: "#94a3b8" },
];

const fallbackSeatTypes = [
  { id: "default", name: "Standard", price: 0, color: "#2563eb" },
];

const layoutPresets = [
  { value: "stage-balcony", label: "Stage + balcony / side rows" },
  { value: "orchestra", label: "Orchestra bowl + side rows" },
  { value: HOLD_LAYOUT_PRESET, label: "Current event holds layout", eventId: HOLD_LAYOUT_EVENT_ID },
  {
    value: HOLD_STAGE_BALCONY_PRESET,
    label: "Current event balcony holds layout",
    eventId: HOLD_LAYOUT_EVENT_ID,
  },
  {
    value: FOUR_TYPE_ORCHESTRA_PRESET,
    label: "Current event orchestra layout",
    eventId: FOUR_TYPE_ORCHESTRA_EVENT_ID,
  },
  {
    value: COMBINED_FULL_VENUE_PRESET,
    label: "Combined full venue layout",
    eventId: FOUR_TYPE_ORCHESTRA_EVENT_ID,
  },
  {
    value: SIX_TIER_FULL_VENUE_PRESET,
    label: "Six tier full venue layout",
    eventId: SIX_TIER_FULL_VENUE_EVENT_ID,
  },
];

function snapCoordinate(value, enabled, size = 10) {
  if (!enabled) return Math.round(value);
  return Math.round(value / size) * size;
}

function getSeatCanvasPosition(seat) {
  return {
    x: Number.isFinite(seat.layoutX) ? seat.layoutX : seat.column * SEAT_SPACING,
    y: Number.isFinite(seat.layoutY) ? seat.layoutY : seat.row * SEAT_SPACING,
  };
}

function normalizeSeatStatus(status) {
  if (status === "taken") return "sold";
  if (status === "available") return "pending";
  if (status === "pending" || status === "reserved" || status === "sold" || status === "disabled") {
    return status;
  }
  return "pending";
}

function toFirestoreStatus(status) {
  if (status === "available" || status === "pending") return "pending";
  if (status === "sold") return "taken";
  if (status === "reserved") return "reserved";
  if (status === "disabled") return "disabled";
  return "pending";
}

function getSeatTypeColor(name, index = 0) {
  const colorMap = {
    Standard: "#2563eb",
    Premium: "#16a34a",
    VIP: "#f97316",
    Wheelchair: "#7c3aed",
    Economy: "#0891b2",
    Business: "#9333ea",
    "First Class": "#ca8a04",
  };

  const palette = ["#2563eb", "#16a34a", "#f97316", "#7c3aed", "#0891b2", "#ca8a04"];
  return colorMap[name] || palette[index % palette.length];
}

function getEventSeatTypeColor(eventId, name, index = 0) {
  if (eventId === SIX_TIER_FULL_VENUE_EVENT_ID) {
    const tierMatch = String(name || "").match(/tier\s*([1-6])\b/i);
    if (tierMatch) return SIX_TIER_COLORS[Number(tierMatch[1])];
  }

  if (eventId === FOUR_TYPE_ORCHESTRA_EVENT_ID) {
    return FOUR_TYPE_ORCHESTRA_COLORS[index] || FOUR_TYPE_ORCHESTRA_COLORS[0];
  }

  return getSeatTypeColor(name, index);
}

function getRenderedSeatColor(seat, seatType, status) {
  if (seat.status === "pending") {
    if (seat.color && seat.color !== HOLD_OPEN_COLOR) return seat.color;
    return seatType.color || fallbackSeatTypes[0].color;
  }

  if (seat.status === "reserved") return seatType.color || seat.color || status.color;
  return status.color;
}

function escapeSeatDomId(id) {
  if (window.CSS?.escape) return window.CSS.escape(id);
  return String(id).replace(/["\\]/g, "\\$&");
}

function getSeatDomNodes(canvasElement, seatId) {
  const seatNode = canvasElement.querySelector(`#${escapeSeatDomId(seatId)}`);
  const shapeNode = seatNode?.querySelector(SEAT_SHAPE_SELECTOR);
  return { seatNode, shapeNode };
}

function getSeatRowLabel(seat) {
  const fromLabel = seat.rowLabel || String(seat.name || "").replace(/[^A-Za-z]+/g, "");
  if (fromLabel) return fromLabel.toUpperCase();
  return String.fromCharCode(65 + Number(seat.row || 0));
}

function getSeatOrdinal(seat) {
  return Number(seat.seatNumber || seat.column + 1 || 0);
}

function getRowCode(rowLabel) {
  const label = String(rowLabel || "").replace(/[^A-Z]/g, "");
  if (!label) return null;

  return label.split("").reduce((total, character) => total * 26 + character.charCodeAt(0) - 64, 0);
}

function matchesRowSelector(rowLabel, selector) {
  const query = String(selector || "").trim().toUpperCase();
  if (!query) return true;

  const rowCode = getRowCode(rowLabel);
  return query.split(/[,\s]+/).some((token) => {
    if (!token) return false;

    const [start, end] = token.split("-").map((part) => part.trim());
    if (!end) return start === rowLabel;

    const startCode = getRowCode(start);
    const endCode = getRowCode(end);
    if (rowCode === null || startCode === null || endCode === null) return false;

    return rowCode >= Math.min(startCode, endCode) && rowCode <= Math.max(startCode, endCode);
  });
}

function getSeatName(row, column) {
  const rowLetter = String.fromCharCode(65 + row);
  return `${rowLetter}${String(column + 1).padStart(2, "0")}`;
}

function getSeatId(row, column) {
  return `row-${String.fromCharCode(97 + row)}${row + 1}-${String(column + 1).padStart(2, "0")}`;
}

function getSeatTypeIdByName(seatTypes, searchText, fallbackIndex = 0) {
  const foundType = seatTypes.find((type) =>
    type.name.toLowerCase().includes(searchText.toLowerCase())
  );
  return foundType?.id || seatTypes[fallbackIndex]?.id || seatTypes[0]?.id || "default";
}

function getSeatTypeIdByIndex(seatTypes, index) {
  return seatTypes[index]?.id || seatTypes[0]?.id || "default";
}

function getTierSeatTypeId(seatTypes, tierNumber) {
  const tierPattern = new RegExp(`\\btier\\s*${tierNumber}\\b`, "i");
  const foundType = seatTypes.find((type) => tierPattern.test(type.name || ""));
  return foundType?.id || getSeatTypeIdByIndex(seatTypes, tierNumber - 1);
}

function createDefaultSeats(rows, columns, seatTypeId) {
  const seats = [];

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const name = getSeatName(row, column);
      seats.push({
        id: getSeatId(row, column),
        name,
        displayTitle: name,
        row,
        column,
        type: seatTypeId,
        priceId: seatTypeId,
        status: "pending",
      });
    }
  }

  return seats;
}

function createLayoutSeat({
  section,
  rowLabel,
  seatNumber,
  x,
  y,
  type,
  rowIndex,
  color,
  status = "pending",
  name,
}) {
  const safeSection = section.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const safeRow = rowLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const seatName = name || `${rowLabel}${seatNumber}`;

  return {
    id: `${safeSection}-${safeRow}-${String(seatNumber).padStart(2, "0")}`,
    name: seatName,
    displayTitle: seatName,
    rowLabel,
    section,
    seatNumber,
    row: rowIndex,
    column: seatNumber - 1,
    layoutX: x,
    layoutY: y,
    type,
    priceId: type,
    color,
    status,
  };
}

function pushHorizontalRow(seats, config) {
  const {
    section,
    rowLabel,
    count,
    startX,
    y,
    type,
    rowIndex,
    spacing = SEAT_SPACING,
    curve = 0,
    color,
  } = config;

  for (let index = 0; index < count; index += 1) {
    const middle = (count - 1) / 2;
    const curveOffset = curve ? Math.pow(index - middle, 2) * curve : 0;
    seats.push(
      createLayoutSeat({
        section,
        rowLabel,
        seatNumber: index + 1,
        x: startX + index * spacing,
        y: y + curveOffset,
        type,
        rowIndex,
        color,
      })
    );
  }
}

function pushVerticalRow(seats, config) {
  const {
    section,
    rowLabel,
    count,
    x,
    startY,
    type,
    rowIndex,
    spacing = SEAT_SPACING,
    color,
    seatNumberStart = 1,
    namePrefix,
    nameSuffix,
  } = config;

  for (let index = 0; index < count; index += 1) {
    const seatNumber = seatNumberStart + index;
    seats.push(
      createLayoutSeat({
        section,
        rowLabel,
        seatNumber,
        x,
        y: startY + index * spacing,
        type,
        rowIndex,
        color,
        name: namePrefix
          ? `${namePrefix}-${seatNumber}${nameSuffix ? `-${nameSuffix}` : ""}`
          : undefined,
      })
    );
  }
}

function createStageBalconyLayout(seatTypes) {
  const seats = [];
  const sideType = getSeatTypeIdByName(seatTypes, "vip", 0);
  const balconyType = getSeatTypeIdByName(seatTypes, "premium", 1);
  const orchestraType = getSeatTypeIdByName(seatTypes, "standard", 0);

  [
    { rowLabel: "GG", count: 23, startX: 220, y: 20 },
    { rowLabel: "FF", count: 21, startX: 245, y: 58 },
    { rowLabel: "EE", count: 22, startX: 210, y: 96 },
  ].forEach((row, index) => {
    pushHorizontalRow(seats, {
      ...row,
      section: "Balcony",
      type: balconyType,
      rowIndex: index,
      color: "#6b21a8",
    });
  });

  pushVerticalRow(seats, {
    section: "Left Side 5",
    rowLabel: "5",
    count: 12,
    x: 88,
    startY: 255,
    type: sideType,
    rowIndex: 10,
    color: "#6b21a8",
    namePrefix: "5",
    nameSuffix: "A",
  });
  pushVerticalRow(seats, {
    section: "Left Side 7",
    rowLabel: "7",
    count: 18,
    x: 120,
    startY: 735,
    type: sideType,
    rowIndex: 11,
    color: "#6b21a8",
    namePrefix: "7",
    nameSuffix: "A",
  });
  pushVerticalRow(seats, {
    section: "Right Side 6",
    rowLabel: "6",
    count: 12,
    x: 1035,
    startY: 255,
    type: sideType,
    rowIndex: 12,
    color: "#6b21a8",
    namePrefix: "6",
    nameSuffix: "B",
  });
  pushVerticalRow(seats, {
    section: "Right Side 8",
    rowLabel: "8",
    count: 18,
    x: 1000,
    startY: 735,
    type: sideType,
    rowIndex: 13,
    color: "#6b21a8",
    namePrefix: "8",
    nameSuffix: "B",
  });

  [
    { rowLabel: "AA", count: 24, startX: 190, y: 1420 },
    { rowLabel: "BB", count: 24, startX: 205, y: 1468 },
    { rowLabel: "CC", count: 23, startX: 235, y: 1516 },
    { rowLabel: "DD", count: 22, startX: 265, y: 1564 },
  ].forEach((row, index) => {
    pushHorizontalRow(seats, {
      ...row,
      section: "Lower Orchestra",
      type: orchestraType,
      rowIndex: 30 + index,
      color: "#0f8b96",
    });
  });

  ["A", "B", "C", "D"].forEach((rowLabel, index) => {
    const seatNumber = 31 + index;

    seats.push(
      createLayoutSeat({
        section: "Left Lower Side 9",
        rowLabel,
        seatNumber,
        name: `9-${rowLabel}-${seatNumber}`,
        x: 84,
        y: 1328 + index * 34,
        type: orchestraType,
        rowIndex: 45 + index,
        color: "#0f8b96",
      })
    );
    seats.push(
      createLayoutSeat({
        section: "Right Lower Side 10",
        rowLabel,
        seatNumber,
        name: `10-${rowLabel}-${seatNumber}`,
        x: 1035,
        y: 1328 + index * 34,
        type: orchestraType,
        rowIndex: 55 + index,
        color: "#0f8b96",
      })
    );
  });

  return seats;
}

function createOrchestraLayout(seatTypes) {
  const seats = [];
  const blueType = getSeatTypeIdByName(seatTypes, "standard", 0);
  const orangeType = getSeatTypeIdByName(seatTypes, "premium", 1);
  const redType = getSeatTypeIdByName(seatTypes, "vip", 2);
  const purpleType = getSeatTypeIdByName(seatTypes, "balcony", 3);
  const rowCounts = [
    ["A", 17, blueType, "#003bd1"],
    ["B", 16, blueType, "#003bd1"],
    ["C", 19, blueType, "#003bd1"],
    ["D", 18, orangeType, "#f8bd83"],
    ["E", 19, orangeType, "#f8bd83"],
    ["F", 18, orangeType, "#f8bd83"],
    ["G", 19, orangeType, "#e77917"],
    ["H", 18, orangeType, "#e77917"],
    ["J", 19, orangeType, "#e77917"],
    ["K", 18, orangeType, "#e77917"],
    ["L", 19, redType, "#d71920"],
    ["M", 18, redType, "#d71920"],
    ["N", 17, redType, "#d71920"],
    ["O", 14, redType, "#d71920"],
  ];

  rowCounts.forEach(([rowLabel, count, type, color], index) => {
    pushHorizontalRow(seats, {
      section: "Main Orchestra",
      rowLabel,
      count,
      startX: 285 + Math.abs(7 - index) * 9,
      y: 275 + index * 44,
      type,
      rowIndex: index,
      curve: 0.16,
      color,
    });
  });

  [
    ["P", 19, 300, 1010],
    ["Q", 19, 315, 1070],
    ["R", 23, 250, 1130],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Rear Orchestra",
      rowLabel,
      count,
      startX,
      y,
      type: purpleType,
      rowIndex: 40 + index,
      color: "#6b21a8",
    });
  });

  "ABCDEFGHIJKLMNO".replace("I", "").split("").forEach((rowLabel, index) => {
    const color = index < 2 ? "#f8bd83" : index < 6 ? "#e77917" : index < 10 ? "#d71920" : "#6b21a8";
    const type = index < 2 ? orangeType : index < 6 ? orangeType : index < 10 ? redType : purpleType;
    seats.push(
      createLayoutSeat({
        section: "Left Side",
        rowLabel,
        seatNumber: index + 1,
        x: 135,
        y: 260 + index * 47,
        type,
        rowIndex: 70 + index,
        color,
      })
    );
    seats.push(
      createLayoutSeat({
        section: "Right Side",
        rowLabel,
        seatNumber: index + 15,
        x: 1070,
        y: 260 + index * 47,
        type,
        rowIndex: 90 + index,
        color,
      })
    );
  });

  return seats;
}

function createFourTypeOrchestraLayout(seatTypes) {
  const seats = [];
  const typeOne = getSeatTypeIdByIndex(seatTypes, 0);
  const typeTwo = getSeatTypeIdByIndex(seatTypes, 1);
  const typeThree = getSeatTypeIdByIndex(seatTypes, 2);
  const typeFour = getSeatTypeIdByIndex(seatTypes, 3);
  const [typeOneColor, typeTwoColor, typeThreeColor, typeFourColor] = FOUR_TYPE_ORCHESTRA_COLORS;

  const rowGroups = [
    ["A", 17, typeOne, typeOneColor],
    ["B", 16, typeOne, typeOneColor],
    ["C", 19, typeOne, typeOneColor],
    ["D", 18, typeTwo, typeTwoColor],
    ["E", 19, typeTwo, typeTwoColor],
    ["F", 18, typeTwo, typeTwoColor],
    ["G", 19, typeTwo, typeTwoColor],
    ["H", 18, typeTwo, typeTwoColor],
    ["J", 19, typeTwo, typeTwoColor],
    ["K", 18, typeTwo, typeTwoColor],
    ["L", 19, typeThree, typeThreeColor],
    ["M", 18, typeThree, typeThreeColor],
    ["N", 17, typeThree, typeThreeColor],
    ["O", 14, typeThree, typeThreeColor],
  ];

  rowGroups.forEach(([rowLabel, count, type, color], index) => {
    pushHorizontalRow(seats, {
      section: "Main Orchestra",
      rowLabel,
      count,
      startX: 285 + Math.abs(7 - index) * 9,
      y: 275 + index * 44,
      type,
      rowIndex: index,
      curve: 0.16,
      color,
    });
  });

  [
    ["P", 19, 300, 1010],
    ["Q", 19, 315, 1070],
    ["R", 23, 250, 1130],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Rear Orchestra",
      rowLabel,
      count,
      startX,
      y,
      type: typeFour,
      rowIndex: 40 + index,
      color: typeFourColor,
    });
  });

  "ABCDEFGHIJKLMNO".replace("I", "").split("").forEach((rowLabel, index) => {
    const sideSeatType =
      index < 6 ? typeTwo : index < 10 ? typeThree : index < 14 ? typeFour : typeOne;
    const sideSeatColor =
      index < 6 ? typeTwoColor : index < 10 ? typeThreeColor : index < 14 ? typeFourColor : typeOneColor;

    seats.push(
      createLayoutSeat({
        section: "Left Side",
        rowLabel,
        seatNumber: index + 1,
        x: 135,
        y: 260 + index * 47,
        type: sideSeatType,
        rowIndex: 70 + index,
        color: sideSeatColor,
      })
    );
    seats.push(
      createLayoutSeat({
        section: "Right Side",
        rowLabel,
        seatNumber: index + 15,
        x: 1070,
        y: 260 + index * 47,
        type: sideSeatType,
        rowIndex: 90 + index,
        color: sideSeatColor,
      })
    );
  });

  return seats.map((seat) => ({
    ...seat,
    type: seat.type || typeOne,
    priceId: seat.priceId || seat.type || typeOne,
  }));
}

function createCombinedFullVenueLayout(seatTypes) {
  const seats = [];
  const typeOne = getSeatTypeIdByIndex(seatTypes, 0);
  const typeTwo = getSeatTypeIdByIndex(seatTypes, 1);
  const typeThree = getSeatTypeIdByIndex(seatTypes, 2);
  const typeFour = getSeatTypeIdByIndex(seatTypes, 3);
  const typeFive = getSeatTypeIdByIndex(seatTypes, 4);
  const [blue, orange, red, purple, teal] = COMBINED_FULL_VENUE_COLORS;
  const topRowY = 110;
  const compactRowGap = 40;
  const compactSeatGap = 27;

  [
    ["A", 17, typeOne, blue],
    ["B", 16, typeOne, blue],
    ["C", 19, typeOne, blue],
    ["D", 18, typeTwo, orange],
    ["E", 19, typeTwo, orange],
    ["F", 18, typeTwo, orange],
    ["G", 19, typeTwo, orange],
    ["H", 18, typeTwo, orange],
    ["J", 19, typeTwo, orange],
    ["K", 18, typeTwo, orange],
    ["L", 19, typeThree, red],
    ["M", 18, typeThree, red],
    ["N", 17, typeThree, red],
    ["O", 14, typeThree, red],
  ].forEach(([rowLabel, count, type, color], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Top Bowl",
      rowLabel,
      count,
      startX: 382 + Math.abs(7 - index) * 7,
      y: topRowY + index * compactRowGap,
      type,
      rowIndex: index,
      spacing: compactSeatGap,
      curve: 0.04,
      color,
    });
  });

  [
    ["P", 19, 410, 760],
    ["Q", 19, 428, 815],
    ["R", 23, 360, 870],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Rear Bowl",
      rowLabel,
      count,
      startX,
      y,
      type: typeFour,
      rowIndex: 40 + index,
      spacing: compactSeatGap,
      color: purple,
    });
  });

  [
    ["GG", 23, 360, 990],
    ["FF", 21, 390, 1045],
    ["EE", 22, 375, 1100],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Balcony Rows",
      rowLabel,
      count,
      startX,
      y,
      type: typeFour,
      rowIndex: 60 + index,
      spacing: compactSeatGap,
      color: purple,
    });
  });

  [
    ["Left Upper Side", "5", 12, 300, 1250, 80, "A"],
    ["Right Upper Side", "6", 12, 830, 1250, 90, "B"],
    ["Left Lower Side", "7", 18, 285, 1740, 100, "A"],
    ["Right Lower Side", "8", 18, 845, 1740, 110, "B"],
  ].forEach(([section, rowLabel, count, x, startY, rowIndex, nameSuffix]) => {
    pushVerticalRow(seats, {
      section: `Combined ${section}`,
      rowLabel,
      count,
      x,
      startY,
      type: typeFour,
      rowIndex,
      spacing: 34,
      color: purple,
      namePrefix: rowLabel,
      nameSuffix,
    });
  });

  [
    ["AA", 24, 310, 2500],
    ["BB", 24, 325, 2560],
    ["CC", 23, 355, 2620],
    ["DD", 22, 385, 2680],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Lower Rows",
      rowLabel,
      count,
      startX,
      y,
      type: typeFive,
      rowIndex: 130 + index,
      spacing: compactSeatGap,
      color: teal,
    });
  });

  return seats.map((seat) => ({
    ...seat,
    type: seat.type || typeOne,
    priceId: seat.priceId || seat.type || typeOne,
  }));
}

function createSixTierFullVenueLayout(seatTypes) {
  const seats = [];
  const tierOne = getTierSeatTypeId(seatTypes, 1);
  const tierTwo = getTierSeatTypeId(seatTypes, 2);
  const tierThree = getTierSeatTypeId(seatTypes, 3);
  const tierFour = getTierSeatTypeId(seatTypes, 4);
  const tierFive = getTierSeatTypeId(seatTypes, 5);
  const tierSix = getTierSeatTypeId(seatTypes, 6);
  const topRowY = 110;
  const compactRowGap = 40;
  const compactSeatGap = 27;

  [
    ["A", 17, tierOne, SIX_TIER_COLORS[1]],
    ["B", 16, tierOne, SIX_TIER_COLORS[1]],
    ["C", 19, tierOne, SIX_TIER_COLORS[1]],
    ["D", 18, tierTwo, SIX_TIER_COLORS[2]],
    ["E", 19, tierTwo, SIX_TIER_COLORS[2]],
    ["F", 18, tierTwo, SIX_TIER_COLORS[2]],
    ["G", 19, tierThree, SIX_TIER_COLORS[3]],
    ["H", 18, tierThree, SIX_TIER_COLORS[3]],
    ["J", 19, tierThree, SIX_TIER_COLORS[3]],
    ["K", 18, tierThree, SIX_TIER_COLORS[3]],
    ["L", 19, tierFour, SIX_TIER_COLORS[4]],
    ["M", 18, tierFour, SIX_TIER_COLORS[4]],
    ["N", 17, tierFour, SIX_TIER_COLORS[4]],
    ["O", 14, tierFour, SIX_TIER_COLORS[4]],
  ].forEach(([rowLabel, count, type, color], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Top Bowl",
      rowLabel,
      count,
      startX: 382 + Math.abs(7 - index) * 7,
      y: topRowY + index * compactRowGap,
      type,
      rowIndex: index,
      spacing: compactSeatGap,
      curve: 0.04,
      color,
    });
  });

  "ABCDEFGHIJKLMNO".replace("I", "").split("").forEach((rowLabel, index) => {
    const tierType =
      index < 2 ? tierTwo : index < 6 ? tierThree : index < 10 ? tierFour : tierFive;
    const tierColor =
      index < 2
        ? SIX_TIER_COLORS[2]
        : index < 6
        ? SIX_TIER_COLORS[3]
        : index < 10
        ? SIX_TIER_COLORS[4]
        : SIX_TIER_COLORS[5];

    seats.push(
      createLayoutSeat({
        section: "Combined Top Left Side",
        rowLabel,
        seatNumber: index + 1,
        x: 285,
        y: topRowY + index * compactRowGap,
        type: tierType,
        rowIndex: 20 + index,
        color: tierColor,
      })
    );
    seats.push(
      createLayoutSeat({
        section: "Combined Top Right Side",
        rowLabel,
        seatNumber: index + 15,
        x: 1025,
        y: topRowY + index * compactRowGap,
        type: tierType,
        rowIndex: 35 + index,
        color: tierColor,
      })
    );
  });

  [
    ["P", 19, 410, 760],
    ["Q", 19, 428, 815],
    ["R", 23, 360, 870],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Rear Bowl",
      rowLabel,
      count,
      startX,
      y,
      type: tierFive,
      rowIndex: 50 + index,
      spacing: compactSeatGap,
      color: SIX_TIER_COLORS[5],
    });
  });

  [
    ["GG", 23, 360, 990],
    ["FF", 21, 390, 1045],
    ["EE", 22, 375, 1100],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Balcony Rows",
      rowLabel,
      count,
      startX,
      y,
      type: tierFive,
      rowIndex: 70 + index,
      spacing: compactSeatGap,
      color: SIX_TIER_COLORS[5],
    });
  });

  [
    ["Left Upper Side", "5", 12, 300, 1250, 80, "A"],
    ["Right Upper Side", "6", 12, 830, 1250, 90, "B"],
    ["Left Lower Side", "7", 18, 285, 1740, 100, "A"],
    ["Right Lower Side", "8", 18, 845, 1740, 110, "B"],
  ].forEach(([section, rowLabel, count, x, startY, rowIndex, nameSuffix]) => {
    pushVerticalRow(seats, {
      section: `Combined ${section}`,
      rowLabel,
      count,
      x,
      startY,
      type: tierFive,
      rowIndex,
      spacing: 34,
      color: SIX_TIER_COLORS[5],
      namePrefix: rowLabel,
      nameSuffix,
    });
  });

  ["A", "B", "C", "D"].forEach((rowLabel, index) => {
    const seatNumber = 31 + index;

    seats.push(
      createLayoutSeat({
        section: "Combined Lower Left Side",
        rowLabel,
        seatNumber,
        name: `9-${rowLabel}-${seatNumber}`,
        x: 270,
        y: 2380 + index * 42,
        type: tierSix,
        rowIndex: 120 + index,
        color: SIX_TIER_COLORS[6],
      })
    );
    seats.push(
      createLayoutSeat({
        section: "Combined Lower Right Side",
        rowLabel,
        seatNumber,
        name: `10-${rowLabel}-${seatNumber}`,
        x: 900,
        y: 2380 + index * 42,
        type: tierSix,
        rowIndex: 125 + index,
        color: SIX_TIER_COLORS[6],
      })
    );
  });

  [
    ["AA", 24, 310, 2500],
    ["BB", 24, 325, 2560],
    ["CC", 23, 355, 2620],
    ["DD", 22, 385, 2680],
  ].forEach(([rowLabel, count, startX, y], index) => {
    pushHorizontalRow(seats, {
      section: "Combined Lower Rows",
      rowLabel,
      count,
      startX,
      y,
      type: tierSix,
      rowIndex: 130 + index,
      spacing: compactSeatGap,
      color: SIX_TIER_COLORS[6],
    });
  });

  return seats.map((seat) => ({
    ...seat,
    type: seat.type || tierOne,
    priceId: seat.priceId || seat.type || tierOne,
  }));
}

function applyFourTypeOrchestraTier(seat, seatTypes) {
  const typeOne = getSeatTypeIdByIndex(seatTypes, 0);
  const typeTwo = getSeatTypeIdByIndex(seatTypes, 1);
  const typeThree = getSeatTypeIdByIndex(seatTypes, 2);
  const typeFour = getSeatTypeIdByIndex(seatTypes, 3);
  const rowLabel = (seat.rowLabel || seat.name || "").replace(/[^A-Za-z]/g, "").charAt(0).toUpperCase();
  const section = (seat.section || "").toLowerCase();

  let nextType = typeOne;
  if (section.includes("side")) {
    if ("ABCDEF".includes(rowLabel)) nextType = typeTwo;
    else if ("GHJK".includes(rowLabel)) nextType = typeThree;
    else if ("LMNO".includes(rowLabel)) nextType = typeFour;
  } else if ("ABC".includes(rowLabel)) {
    nextType = typeOne;
  } else if ("DEFGHJK".includes(rowLabel)) {
    nextType = typeTwo;
  } else if ("LMNO".includes(rowLabel)) {
    nextType = typeThree;
  } else if ("PQR".includes(rowLabel)) {
    nextType = typeFour;
  }

  return {
    ...seat,
    type: nextType,
    priceId: nextType,
    color: "",
  };
}

const holdOpenSeatCountByRow = {
  A: 8,
  B: 8,
  C: 9,
  D: 9,
  E: 9,
  F: 9,
  G: 9,
  H: 9,
  J: 9,
  K: 9,
  L: 9,
  M: 9,
  N: 8,
  O: 6,
  P: 10,
  Q: 10,
  R: 12,
  GG: 12,
  FF: 11,
  EE: 11,
  AA: 12,
  BB: 12,
  CC: 12,
  DD: 11,
};

function isLeftOpenHoldSeat(seat) {
  const section = seat.section || "";
  const normalizedSection = section.toLowerCase();
  const rowLabel = getSeatRowLabel(seat);
  const seatNumber = getSeatOrdinal(seat);

  if (normalizedSection.includes("right")) return false;
  if (normalizedSection.includes("left")) return true;

  const openSeatCount = holdOpenSeatCountByRow[rowLabel];
  return Boolean(openSeatCount && seatNumber <= openSeatCount);
}

function getHoldOriginalTierNumber(seat) {
  const section = (seat.section || "").toLowerCase();
  const rowLabel = getSeatRowLabel(seat);
  const rowInitial = rowLabel.charAt(0);

  if (section.includes("lower")) return 6;
  if (section.includes("balcony") || section.includes("rear")) return 5;

  if (section.includes("side")) {
    if ("AB".includes(rowInitial)) return 2;
    if ("CDEF".includes(rowInitial)) return 3;
    if ("GHJK".includes(rowInitial)) return 4;
    return 5;
  }

  if ("ABC".includes(rowInitial)) return 1;
  if ("DEF".includes(rowInitial)) return 2;
  if ("GHJK".includes(rowInitial)) return 3;
  if ("LMNO".includes(rowInitial)) return 4;
  if ("PQR".includes(rowInitial)) return 5;

  return 1;
}

function getHoldOriginalSeatColor(seat) {
  return SIX_TIER_COLORS[getHoldOriginalTierNumber(seat)] || "";
}

function getHoldOriginalSeatTypeId(seat, seatTypes) {
  const tierNumber = getHoldOriginalTierNumber(seat);
  return getTierSeatTypeId(seatTypes, tierNumber);
}

function applyLeftOpenHoldStatus(seats, seatTypes) {
  return seats.map((seat) => {
    if (isLeftOpenHoldSeat(seat)) {
      const originalType = getHoldOriginalSeatTypeId(seat, seatTypes);

      return {
        ...seat,
        type: originalType,
        priceId: originalType,
        color: getHoldOriginalSeatColor(seat),
        displayTitle: seat.name,
        status: "pending",
      };
    }

    return {
      ...seat,
      status: "reserved",
      ticketId: "",
    };
  });
}

function createEventHoldLayout(seatTypes) {
  return applyLeftOpenHoldStatus(createOrchestraLayout(seatTypes), seatTypes);
}

function createEventStageBalconyHoldLayout(seatTypes) {
  return applyLeftOpenHoldStatus(createStageBalconyLayout(seatTypes), seatTypes);
}

function createPresetSeats(preset, seatTypes) {
  if (preset === HOLD_LAYOUT_PRESET) return createEventHoldLayout(seatTypes);
  if (preset === HOLD_STAGE_BALCONY_PRESET) return createEventStageBalconyHoldLayout(seatTypes);
  if (preset === SIX_TIER_FULL_VENUE_PRESET) return createSixTierFullVenueLayout(seatTypes);
  if (preset === COMBINED_FULL_VENUE_PRESET) return createCombinedFullVenueLayout(seatTypes);
  if (preset === FOUR_TYPE_ORCHESTRA_PRESET) return createFourTypeOrchestraLayout(seatTypes);
  if (preset === "orchestra") return createOrchestraLayout(seatTypes);
  return createStageBalconyLayout(seatTypes);
}

export default function SeatSelectionV2() {
  const { eventId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const seatmapRef = useRef(null);
  const draggingRef = useRef(null);
  const bulkSelectionDragRef = useRef(null);
  const lastPointerEventRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [eventData, setEventData] = useState(null);
  const [seatTypes, setSeatTypes] = useState(fallbackSeatTypes);
  const [seats, setSeats] = useState([]);
  const [selectedSeatId, setSelectedSeatId] = useState("");
  const [rowsToCreate, setRowsToCreate] = useState(DEFAULT_ROWS);
  const [columnsToCreate, setColumnsToCreate] = useState(DEFAULT_COLUMNS);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedPreset, setSelectedPreset] = useState(
    eventId === HOLD_LAYOUT_EVENT_ID
      ? HOLD_LAYOUT_PRESET
      : eventId === SIX_TIER_FULL_VENUE_EVENT_ID
      ? SIX_TIER_FULL_VENUE_PRESET
      : eventId === FOUR_TYPE_ORCHESTRA_EVENT_ID
      ? COMBINED_FULL_VENUE_PRESET
      : "stage-balcony"
  );
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [planFullscreen, setPlanFullscreen] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkSelectedSeatIds, setBulkSelectedSeatIds] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null);
  const [smartRows, setSmartRows] = useState("");
  const [smartSeatFrom, setSmartSeatFrom] = useState("");
  const [smartSeatTo, setSmartSeatTo] = useState("");
  const [smartSection, setSmartSection] = useState("all");
  const [smartStatus, setSmartStatus] = useState("all");
  const [smartType, setSmartType] = useState("all");

  const selectedSeat = useMemo(
    () => seats.find((seat) => seat.id === selectedSeatId) || null,
    [selectedSeatId, seats]
  );
  const bulkSelectedSeatSet = useMemo(
    () => new Set(bulkSelectedSeatIds),
    [bulkSelectedSeatIds]
  );

  const availableLayoutPresets = useMemo(() => {
    return layoutPresets.filter((preset) => !preset.eventId || preset.eventId === eventId);
  }, [eventId]);

  useEffect(() => {
    const hasSelectedPreset = availableLayoutPresets.some((preset) => preset.value === selectedPreset);
    if (!hasSelectedPreset && availableLayoutPresets[0]) {
      setSelectedPreset(availableLayoutPresets[0].value);
    }
  }, [availableLayoutPresets, selectedPreset]);

  const typeById = useMemo(() => {
    return seatTypes.reduce((acc, type) => {
      acc[type.id] = type;
      return acc;
    }, {});
  }, [seatTypes]);

  const visibleSeats = useMemo(() => {
    return seats.filter((seat) => {
      const matchesStatus = statusFilter === "all" || seat.status === statusFilter;
      const matchesType = typeFilter === "all" || seat.type === typeFilter;
      return matchesStatus && matchesType;
    });
  }, [seats, statusFilter, typeFilter]);

  const seatCounts = useMemo(() => {
    return statusOptions.reduce((acc, option) => {
      acc[option.value] = seats.filter((seat) => seat.status === option.value).length;
      return acc;
    }, {});
  }, [seats]);

  const sectionOptions = useMemo(() => {
    const sections = seats.map((seat) => seat.section).filter(Boolean);
    return Array.from(new Set(sections)).sort();
  }, [seats]);

  const canvasData = useMemo(() => {
    const isCustomLayout = visibleSeats.some(
      (seat) => Number.isFinite(seat.layoutX) && Number.isFinite(seat.layoutY)
    );
    const isCombinedLayout = visibleSeats.some((seat) => (seat.section || "").startsWith("Combined"));
    const labelsByRow = new Map();
    const customRowGroups = new Map();

    const canvasSeats = visibleSeats.map((seat) => {
      const seatType = typeById[seat.type] || typeById[seat.priceId] || fallbackSeatTypes[0];
      const status = statusOptions.find((option) => option.value === seat.status) || statusOptions[0];
      const isSelectable = seat.status === "pending";
      const renderedSeatColor = getRenderedSeatColor(seat, seatType, status);
      const seatX = Number.isFinite(seat.layoutX) ? seat.layoutX : seat.column * SEAT_SPACING;
      const seatY = Number.isFinite(seat.layoutY) ? seat.layoutY : seat.row * SEAT_SPACING;

      if (isCustomLayout) {
        const groupKey = `${seat.section || "main"}-${seat.rowLabel || seat.row}-${Math.round(seatY)}`;
        const group = customRowGroups.get(groupKey) || {
          section: seat.section || "Main",
          rowLabel: seat.rowLabel || seat.name.replace(/[0-9-]+$/g, ""),
          seats: [],
        };
        group.seats.push({ x: seatX, y: seatY });
        customRowGroups.set(groupKey, group);
      } else if (!labelsByRow.has(seat.row)) {
        labelsByRow.set(seat.row, {
          title: seat.name.replace(/[0-9-]+$/g, "") || String.fromCharCode(65 + seat.row),
          x: -32,
          y: seat.row * SEAT_SPACING,
        });
      }

      return {
        id: seat.id,
        title: seat.name,
        x: seatX,
        y: seatY,
        salable: isSelectable,
        selected: seat.id === selectedSeatId,
        color: renderedSeatColor,
        note: `${seat.name} • ${seatType.name} • ${status.label}`,
        custom_data: {
          seatId: seat.id,
          status: seat.status,
          type: seat.type,
        },
      };
    });

    if (isCustomLayout) {
      customRowGroups.forEach((group) => {
        if (group.seats.length === 0) return;
        const minX = Math.min(...group.seats.map((seat) => seat.x));
        const maxX = Math.max(...group.seats.map((seat) => seat.x));
        const avgY = group.seats.reduce((sum, seat) => sum + seat.y, 0) / group.seats.length;
        const centerX = (minX + maxX) / 2;

        labelsByRow.set(`${group.section}-${group.rowLabel}-left-${avgY}`, {
          title: group.rowLabel,
          x: minX - 40,
          y: avgY,
        });
        labelsByRow.set(`${group.section}-${group.rowLabel}-right-${avgY}`, {
          title: group.rowLabel,
          x: maxX + 40,
          y: avgY,
        });

        if (!isCombinedLayout && group.seats.length >= 8) {
          labelsByRow.set(`${group.section}-${group.rowLabel}-center-${avgY}`, {
            title: `ROW ${group.rowLabel}`,
            x: centerX,
            y: avgY - 22,
          });
        }
      });

      if (isCombinedLayout) {
        labelsByRow.set("combined-stage-top", { title: "Stage", x: 560, y: 35 });
        labelsByRow.set("combined-stage-middle", { title: "Stage", x: 560, y: 1460 });
      } else if (visibleSeats.some((seat) => (seat.section || "").toLowerCase().includes("balcony"))) {
        labelsByRow.set("venue-balcony", { title: "BALCONY", x: 545, y: -55 });
        labelsByRow.set("venue-stage", { title: "Stage", x: 560, y: 505 });
      }

      if (visibleSeats.some((seat) => (seat.section || "").toLowerCase().includes("orchestra"))) {
        labelsByRow.set("venue-stage-top", { title: "Stage", x: 560, y: 65 });
        labelsByRow.set("venue-orchestra", { title: "ORCHESTRA", x: 560, y: 1210 });
      }
    }

    return {
      blocks: [
        {
          id: "main-floor",
          title: eventData?.title || eventData?.eventTitle || "Seat Map",
          color: "#f8fafc",
          border_color: "#d7dee8",
          labels: Array.from(labelsByRow.values()),
          seats: canvasSeats,
        },
      ],
    };
  }, [eventData, selectedSeatId, typeById, visibleSeats]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadEventData = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const eventDoc = await fires.collection("events").doc(eventId).get();
      if (!eventDoc.exists) {
        setError("Event not found");
        return;
      }

      const eventInfo = eventDoc.data();
      setEventData(eventInfo);

      const priceEntries = eventInfo.prices ? Object.entries(eventInfo.prices) : [];
      const savedSeatTypeColors = eventInfo.seatTypeColors || {};
      const extractedSeatTypes =
        priceEntries.length > 0
          ? priceEntries.map(([priceKey, price], index) => {
              const name = price.priceDescription || price.name || `Ticket Type ${index + 1}`;
              const id = price.id || price.priceId || priceKey || `price-${index}`;
              const eventColor = getEventSeatTypeColor(eventId, name, index);

              return {
                id,
                name,
                price: price.price || 0,
                color:
                  eventId === SIX_TIER_FULL_VENUE_EVENT_ID
                    ? savedSeatTypeColors[id] || eventColor
                    : savedSeatTypeColors[id] || price.color || eventColor,
              };
            })
          : fallbackSeatTypes;

      setSeatTypes(extractedSeatTypes);

      const seatSnapshot = await fires.collection("events").doc(eventId).collection("seats").get();
      const loadedSeats = seatSnapshot.docs.map((doc) => {
        const seatData = doc.data();
        const row = seatData.x !== undefined ? seatData.x : seatData.row || 0;
        const column = seatData.y !== undefined ? seatData.y : seatData.column || 0;
        const type = seatData.priceId || seatData.type || extractedSeatTypes[0]?.id || "default";
        const name = seatData.name || getSeatName(row, column);

        return {
          id: doc.id || seatData.id || getSeatId(row, column),
          name,
          row,
          column,
          seatNumber: seatData.seatNumber || column + 1,
          type,
          priceId: type,
          layoutX: Number.isFinite(seatData.layoutX) ? seatData.layoutX : undefined,
          layoutY: Number.isFinite(seatData.layoutY) ? seatData.layoutY : undefined,
          rowLabel: seatData.rowLabel || "",
          section: seatData.section || "",
          displayTitle: name,
          color: seatData.color || "",
          status: normalizeSeatStatus(seatData.status),
          ticketId: seatData.ticketId || "",
        };
      });

      const nextSeats =
        loadedSeats.length > 0
          ? eventId === FOUR_TYPE_ORCHESTRA_EVENT_ID
            ? loadedSeats.map((seat) =>
                (seat.section || "").startsWith("Combined")
                  ? seat
                  : applyFourTypeOrchestraTier(seat, extractedSeatTypes)
              )
            : loadedSeats
          : eventId === FOUR_TYPE_ORCHESTRA_EVENT_ID
          ? createCombinedFullVenueLayout(extractedSeatTypes)
          : eventId === SIX_TIER_FULL_VENUE_EVENT_ID
          ? createSixTierFullVenueLayout(extractedSeatTypes)
          : createDefaultSeats(DEFAULT_ROWS, DEFAULT_COLUMNS, extractedSeatTypes[0]?.id || "default");

      setSeats(nextSeats);
      setSelectedSeatId(nextSeats[0]?.id || "");
    } catch (err) {
      setError(`Error loading seat map: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    loadEventData();
  }, [currentUser, loadEventData, navigate]);

  useEffect(() => {
    if (!planFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setPlanFullscreen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [planFullscreen]);

  useEffect(() => {
    if (!seatmapRef.current) return;

    window.setTimeout(() => {
      seatmapRef.current?.windowManager?.resizeHandler?.();
      seatmapRef.current?.zoomManager?.zoomToVenue?.(false, true);
      if (layoutEditMode) seatmapRef.current?.zoomManager?.zoomDisable?.();
    }, 60);
  }, [layoutEditMode, planFullscreen]);

  useEffect(() => {
    if (!canvasRef.current || loading) return undefined;

    const canvasElement = canvasRef.current;
    canvasElement.innerHTML = "";

    const seatmap = new SeatMapCanvas(canvasElement, {
      legend: true,
      resizable: true,
      click_enable_sold_seats: false,
      canvas_stageout_control: false,
      style: {
        seat: {
          radius: 11,
          color: "#2563eb",
          hover: "#0f172a",
          selected: "#111827",
          not_salable: "#94a3b8",
          check_color: "#ffffff",
        },
        block: {
          fill: "#f8fafc",
          stroke: "#d7dee8",
          border_width: 1,
          title_color: "#0f172a",
          title_font_size: 16,
        },
        label: {
          color: "#475569",
          bg: "#ffffff",
          font_size: 12,
          radius: 4,
        },
      },
    });

    const capturePointerEvent = (event) => {
      lastPointerEventRef.current = event;
    };

    canvasElement.addEventListener("mousedown", capturePointerEvent, true);

    seatmapRef.current = seatmap;
    seatmap.data.addBulkBlock(canvasData.blocks);

    const applySeatVisuals = () => {
      visibleSeats.forEach((seat) => {
        const seatType = typeById[seat.type] || typeById[seat.priceId] || fallbackSeatTypes[0];
        const status = statusOptions.find((option) => option.value === seat.status) || statusOptions[0];
        const fillColor = getRenderedSeatColor(seat, seatType, status);
        const { seatNode, shapeNode } = getSeatDomNodes(canvasElement, seat.id);
        const isBulkSelected = bulkSelectedSeatSet.has(seat.id);

        if (!seatNode || !shapeNode) return;

        shapeNode.setAttribute("fill", fillColor);
        shapeNode.setAttribute(
          "stroke",
          isBulkSelected ? "#111827" : seat.status === "pending" ? "rgba(15, 23, 42, 0.32)" : "#334155"
        );
        shapeNode.setAttribute("stroke-width", isBulkSelected ? "3" : seat.status === "pending" ? "1" : "2");
        seatNode.style.opacity = seat.status === "pending" || isBulkSelected ? "1" : "0.42";
        seatNode.style.cursor =
          bulkSelectMode ? "crosshair" : seat.status === "pending" || layoutEditMode ? "copy" : "not-allowed";
      });
    };

    const scheduleSeatVisuals = () => {
      window.requestAnimationFrame(applySeatVisuals);
    };

    window.setTimeout(applySeatVisuals, 0);
    canvasElement.addEventListener("mouseover", scheduleSeatVisuals);
    canvasElement.addEventListener("mouseout", scheduleSeatVisuals);
    canvasElement.addEventListener("click", scheduleSeatVisuals);

    const updateSelectionBox = (event) => {
      const drag = bulkSelectionDragRef.current;
      if (!drag) return;

      const canvasRect = canvasElement.getBoundingClientRect();
      const left = Math.min(drag.startClientX, event.clientX) - canvasRect.left;
      const top = Math.min(drag.startClientY, event.clientY) - canvasRect.top;
      const width = Math.abs(event.clientX - drag.startClientX);
      const height = Math.abs(event.clientY - drag.startClientY);

      setSelectionBox({ left, top, width, height });
    };

    const stopBulkSelection = (event) => {
      const drag = bulkSelectionDragRef.current;
      if (!drag) return;

      const minX = Math.min(drag.startClientX, event.clientX);
      const maxX = Math.max(drag.startClientX, event.clientX);
      const minY = Math.min(drag.startClientY, event.clientY);
      const maxY = Math.max(drag.startClientY, event.clientY);
      const selectedIds = visibleSeats
        .filter((seat) => {
          const { shapeNode } = getSeatDomNodes(canvasElement, seat.id);
          if (!shapeNode) return false;

          const rect = shapeNode.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;

          return centerX >= minX && centerX <= maxX && centerY >= minY && centerY <= maxY;
        })
        .map((seat) => seat.id);

      setBulkSelectedSeatIds((currentIds) => {
        if (drag.additive) return Array.from(new Set([...currentIds, ...selectedIds]));
        return selectedIds;
      });
      setSelectionBox(null);
      bulkSelectionDragRef.current = null;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleBulkSelectionMove);
      window.removeEventListener("mouseup", stopBulkSelection);
      window.requestAnimationFrame(applySeatVisuals);
    };

    const handleBulkSelectionMove = (event) => {
      const drag = bulkSelectionDragRef.current;
      if (!drag) return;
      event.preventDefault();
      updateSelectionBox(event);
    };

    const startBulkSelection = (event) => {
      if (!bulkSelectMode || layoutEditMode || event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      bulkSelectionDragRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        additive: event.shiftKey || event.metaKey || event.ctrlKey,
      };
      document.body.style.cursor = "crosshair";
      updateSelectionBox(event);
      window.addEventListener("mousemove", handleBulkSelectionMove);
      window.addEventListener("mouseup", stopBulkSelection);
    };

    canvasElement.addEventListener("mousedown", startBulkSelection, true);

    const getCurrentScale = () => {
      const transform = seatmap.svg?.stage?.node?.attr?.("transform") || "";
      const scaleMatch = transform.match(/scale\(([-0-9.]+)\)/);
      const scale = scaleMatch ? Number(scaleMatch[1]) : 1;
      return Number.isFinite(scale) && scale > 0 ? scale : 1;
    };

    const stopDragging = () => {
      const dragging = draggingRef.current;
      if (!dragging) return;

      const nextX = snapCoordinate(dragging.currentX, snapToGrid);
      const nextY = snapCoordinate(dragging.currentY, snapToGrid);

      setSeats((currentSeats) =>
        currentSeats.map((seat) =>
          seat.id === dragging.seatId
            ? {
                ...seat,
                layoutX: nextX,
                layoutY: nextY,
              }
            : seat
        )
      );
      setSelectedSeatId(dragging.seatId);

      draggingRef.current = null;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleDocumentMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };

    const handleDocumentMouseMove = (event) => {
      const dragging = draggingRef.current;
      if (!dragging) return;

      event.preventDefault();

      const scale = getCurrentScale();
      const nextX = snapCoordinate(
        dragging.startX + (event.clientX - dragging.startClientX) / scale,
        snapToGrid
      );
      const nextY = snapCoordinate(
        dragging.startY + (event.clientY - dragging.startClientY) / scale,
        snapToGrid
      );

      dragging.currentX = nextX;
      dragging.currentY = nextY;
      dragging.node?.attr?.("transform", `translate(${nextX},${nextY})`);
    };

    seatmap.eventManager.addEventListener("SEAT.CLICK", (seatSvg, seatModel) => {
      const clicked = seatModel?.id ? seatModel : seatSvg?.item;
      if (!clicked?.id) return;

      const clickedSeat = seats.find((seat) => seat.id === clicked.id);
      if (clickedSeat?.status !== "pending" && !layoutEditMode) return;

      setSelectedSeatId(clicked.id);
    });
    seatmap.eventManager.addEventListener("SEAT.MOUSEDOWN", (seatSvg, seatModel) => {
      if (!layoutEditMode) return;

      const clicked = seatModel?.id ? seatModel : seatSvg?.item;
      const sourceEvent = lastPointerEventRef.current;
      if (!clicked?.id || !sourceEvent) return;

      const currentSeat = seats.find((seat) => seat.id === clicked.id);
      if (!currentSeat) return;

      const position = getSeatCanvasPosition(currentSeat);
      seatmap.zoomManager?.zoomDisable?.();

      draggingRef.current = {
        seatId: clicked.id,
        startX: position.x,
        startY: position.y,
        currentX: position.x,
        currentY: position.y,
        startClientX: sourceEvent.clientX,
        startClientY: sourceEvent.clientY,
        node: seatSvg?.node,
      };

      document.body.style.cursor = "grabbing";
      sourceEvent.preventDefault?.();
      sourceEvent.stopPropagation?.();
      window.addEventListener("mousemove", handleDocumentMouseMove);
      window.addEventListener("mouseup", stopDragging);
    });

    if (layoutEditMode || bulkSelectMode) {
      seatmap.zoomManager?.zoomDisable?.();
      canvasElement.style.cursor = bulkSelectMode ? "crosshair" : "grab";
    } else {
      seatmap.zoomManager?.zoomEnable?.();
      canvasElement.style.cursor = "";
    }

    window.setTimeout(() => {
      seatmap.windowManager?.resizeHandler?.();
      seatmap.zoomManager?.zoomToVenue?.(false, true);
      if (layoutEditMode) seatmap.zoomManager?.zoomDisable?.();
      applySeatVisuals();
    }, 0);

    return () => {
      draggingRef.current = null;
      bulkSelectionDragRef.current = null;
      setSelectionBox(null);
      document.body.style.cursor = "";
      canvasElement.removeEventListener("mousedown", capturePointerEvent, true);
      canvasElement.removeEventListener("mousedown", startBulkSelection, true);
      canvasElement.removeEventListener("mouseover", scheduleSeatVisuals);
      canvasElement.removeEventListener("mouseout", scheduleSeatVisuals);
      canvasElement.removeEventListener("click", scheduleSeatVisuals);
      window.removeEventListener("mousemove", handleDocumentMouseMove);
      window.removeEventListener("mousemove", handleBulkSelectionMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("mouseup", stopBulkSelection);
      canvasElement.innerHTML = "";
      canvasElement.style.cursor = "";
      seatmapRef.current = null;
    };
  }, [
    bulkSelectMode,
    bulkSelectedSeatSet,
    canvasData,
    layoutEditMode,
    loading,
    seats,
    snapToGrid,
    typeById,
    visibleSeats,
  ]);

  const updateSeat = (seatId, changes) => {
    setSeats((currentSeats) =>
      currentSeats.map((seat) => {
        if (seat.id !== seatId) return seat;

        const nextSeat = { ...seat, ...changes };
        return {
          ...nextSeat,
          displayTitle: nextSeat.name,
        };
      })
    );
  };

  const updateSeatPosition = (seatId, nextX, nextY) => {
    setSeats((currentSeats) =>
      currentSeats.map((seat) =>
        seat.id === seatId
          ? {
              ...seat,
              layoutX: snapCoordinate(nextX, snapToGrid),
              layoutY: snapCoordinate(nextY, snapToGrid),
            }
          : seat
      )
    );
  };

  const handleBulkSelectModeChange = (enabled) => {
    setBulkSelectMode(enabled);
    setSelectionBox(null);
    if (enabled) setLayoutEditMode(false);
  };

  const handleBulkStatusChange = (status) => {
    if (bulkSelectedSeatIds.length === 0) {
      setError("Drag over seats first, then choose a bulk action.");
      return;
    }

    const selectedIds = new Set(bulkSelectedSeatIds);
    setSeats((currentSeats) =>
      currentSeats.map((seat) => (selectedIds.has(seat.id) ? { ...seat, status } : seat))
    );
    setSelectedSeatId(bulkSelectedSeatIds[0] || "");
    setSuccess(
      `${bulkSelectedSeatIds.length} seats marked ${status}. Click Save all seats to publish the change.`
    );
    setError("");
  };

  const handleClearBulkSelection = () => {
    setBulkSelectedSeatIds([]);
    setSelectionBox(null);
  };

  const handleSmartSelect = (additive = false) => {
    const from = smartSeatFrom === "" ? null : Number(smartSeatFrom);
    const to = smartSeatTo === "" ? null : Number(smartSeatTo);

    if ((from !== null && !Number.isFinite(from)) || (to !== null && !Number.isFinite(to))) {
      setError("Seat range must use numbers.");
      return;
    }

    const minSeat = from === null && to === null ? null : Math.min(from ?? to, to ?? from);
    const maxSeat = from === null && to === null ? null : Math.max(from ?? to, to ?? from);
    const matchedIds = seats
      .filter((seat) => {
        const rowLabel = getSeatRowLabel(seat);
        const seatNumber = getSeatOrdinal(seat);
        const matchesRows = matchesRowSelector(rowLabel, smartRows);
        const matchesRange = minSeat === null || (seatNumber >= minSeat && seatNumber <= maxSeat);
        const matchesSection = smartSection === "all" || seat.section === smartSection;
        const matchesStatus = smartStatus === "all" || seat.status === smartStatus;
        const matchesType = smartType === "all" || seat.type === smartType || seat.priceId === smartType;

        return matchesRows && matchesRange && matchesSection && matchesStatus && matchesType;
      })
      .map((seat) => seat.id);

    if (matchedIds.length === 0) {
      setError("No seats matched the smart selector.");
      return;
    }

    setBulkSelectedSeatIds((currentIds) => {
      if (!additive) return matchedIds;
      return Array.from(new Set([...currentIds, ...matchedIds]));
    });
    setSelectedSeatId(matchedIds[0]);
    setSuccess(`${matchedIds.length} seats selected.`);
    setError("");
  };

  const getRightHalfSeatIds = () => {
    const seatsByRow = seats.reduce((acc, seat) => {
      const rowKey = `${seat.section || "Main"}-${getSeatRowLabel(seat)}-${seat.row}`;
      if (!acc[rowKey]) acc[rowKey] = [];
      acc[rowKey].push(seat);
      return acc;
    }, {});

    return Object.values(seatsByRow).flatMap((rowSeats) => {
      const sortedSeats = [...rowSeats].sort((a, b) => {
        const aPosition = getSeatCanvasPosition(a);
        const bPosition = getSeatCanvasPosition(b);
        return aPosition.x - bPosition.x;
      });
      const rightStartIndex = Math.ceil(sortedSeats.length / 2);
      return sortedSeats.slice(rightStartIndex).map((seat) => seat.id);
    });
  };

  const handleSelectRightHalfSeats = () => {
    const rightHalfSeatIds = getRightHalfSeatIds();
    if (rightHalfSeatIds.length === 0) {
      setError("No right-half seats found.");
      return;
    }

    setBulkSelectedSeatIds(rightHalfSeatIds);
    setSelectedSeatId(rightHalfSeatIds[0]);
    setSuccess(`${rightHalfSeatIds.length} right-half seats selected.`);
    setError("");
  };

  const handleReserveRightHalfSeats = () => {
    const rightHalfSeatIds = getRightHalfSeatIds();
    if (rightHalfSeatIds.length === 0) {
      setError("No right-half seats found.");
      return;
    }

    const selectedIds = new Set(rightHalfSeatIds);
    setSeats((currentSeats) =>
      currentSeats.map((seat) => (selectedIds.has(seat.id) ? { ...seat, status: "reserved" } : seat))
    );
    setBulkSelectedSeatIds(rightHalfSeatIds);
    setSelectedSeatId(rightHalfSeatIds[0]);
    setSuccess(`${rightHalfSeatIds.length} right-half seats reserved. Click Save all seats to publish the change.`);
    setError("");
  };

  const handleApplyLeftOpenHoldPattern = () => {
    const nextSeats = applyLeftOpenHoldStatus(seats, seatTypes);
    const openSeatIds = nextSeats.filter((seat) => seat.status === "pending").map((seat) => seat.id);

    if (openSeatIds.length === 0) {
      setError("No hold-open seats matched this layout.");
      return;
    }

    setSeats(nextSeats);
    setBulkSelectedSeatIds(openSeatIds);
    setSelectedSeatId(openSeatIds[0]);
    setStatusFilter("all");
    setSuccess(
      `${openSeatIds.length} left-half seats restored to tier pricing and colors. The right half/rest was reserved. Click Save all seats to publish the change.`
    );
    setError("");
  };

  const handleMoveSelectedSeat = (deltaX, deltaY) => {
    if (!selectedSeat) return;
    const position = getSeatCanvasPosition(selectedSeat);
    updateSeatPosition(selectedSeat.id, position.x + deltaX, position.y + deltaY);
  };

  const handlePositionInputChange = (axis, value) => {
    if (!selectedSeat) return;

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;

    const position = getSeatCanvasPosition(selectedSeat);
    updateSeatPosition(
      selectedSeat.id,
      axis === "x" ? numericValue : position.x,
      axis === "y" ? numericValue : position.y
    );
  };

  const handleTicketTypeColorChange = (typeId, color) => {
    setSeatTypes((currentTypes) =>
      currentTypes.map((type) => (type.id === typeId ? { ...type, color } : type))
    );
  };

  const handleSaveTicketTypeColors = async () => {
    try {
      setSaving(true);
      clearMessages();

      const seatTypeColors = seatTypes.reduce((acc, type) => {
        acc[type.id] = type.color || fallbackSeatTypes[0].color;
        return acc;
      }, {});

      await fires.collection("events").doc(eventId).set(
        {
          seatTypeColors,
          timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
        },
        { merge: true }
      );

      setEventData((currentData) => ({
        ...currentData,
        seatTypeColors,
      }));
      setSuccess("Ticket type colors saved.");
    } catch (err) {
      setError(`Error saving ticket type colors: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDraftMap = () => {
    const rows = Number(rowsToCreate);
    const columns = Number(columnsToCreate);

    if (!Number.isInteger(rows) || rows < 1 || rows > 26) {
      setError("Rows must be between 1 and 26 for A-Z seat naming.");
      return;
    }

    if (!Number.isInteger(columns) || columns < 1 || columns > 100) {
      setError("Columns must be between 1 and 100.");
      return;
    }

    const nextSeats = createDefaultSeats(rows, columns, seatTypes[0]?.id || "default");
    setSeats(nextSeats);
    setSelectedSeatId(nextSeats[0]?.id || "");
    setStatusFilter("all");
    setTypeFilter("all");
    setSuccess(`Created a ${rows} x ${columns} draft seat map. Save all seats to publish it.`);
    setError("");
  };

  const handleCreatePresetMap = () => {
    if (selectedPreset === HOLD_LAYOUT_PRESET && eventId !== HOLD_LAYOUT_EVENT_ID) {
      setError(`This hold layout can only be used for event ${HOLD_LAYOUT_EVENT_ID}.`);
      return;
    }

    if (selectedPreset === HOLD_STAGE_BALCONY_PRESET && eventId !== HOLD_LAYOUT_EVENT_ID) {
      setError(`This balcony hold layout can only be used for event ${HOLD_LAYOUT_EVENT_ID}.`);
      return;
    }

    if (selectedPreset === FOUR_TYPE_ORCHESTRA_PRESET && eventId !== FOUR_TYPE_ORCHESTRA_EVENT_ID) {
      setError(`This orchestra layout can only be used for event ${FOUR_TYPE_ORCHESTRA_EVENT_ID}.`);
      return;
    }

    if (selectedPreset === COMBINED_FULL_VENUE_PRESET && eventId !== FOUR_TYPE_ORCHESTRA_EVENT_ID) {
      setError(`This combined layout can only be used for event ${FOUR_TYPE_ORCHESTRA_EVENT_ID}.`);
      return;
    }

    if (selectedPreset === SIX_TIER_FULL_VENUE_PRESET && eventId !== SIX_TIER_FULL_VENUE_EVENT_ID) {
      setError(`This six tier layout can only be used for event ${SIX_TIER_FULL_VENUE_EVENT_ID}.`);
      return;
    }

    const nextSeats = createPresetSeats(selectedPreset, seatTypes);

    setSeats(nextSeats);
    setSelectedSeatId(nextSeats[0]?.id || "");
    setStatusFilter("all");
    setTypeFilter("all");
    setSuccess(
      `Created ${nextSeats.length} positioned seats from the ${
        availableLayoutPresets.find((preset) => preset.value === selectedPreset)?.label
      } preset. Save all seats to publish it.`
    );
    setError("");
  };

  const handleSaveSeat = async () => {
    if (!selectedSeat) return;

    try {
      setSaving(true);
      clearMessages();

      await fires
        .collection("events")
        .doc(eventId)
        .collection("seats")
        .doc(selectedSeat.id)
        .set(
          {
            id: selectedSeat.id,
            name: selectedSeat.name,
            displayTitle: selectedSeat.name,
            rowLabel: selectedSeat.rowLabel || "",
            section: selectedSeat.section || "",
            x: selectedSeat.row,
            y: selectedSeat.column,
            seatNumber: selectedSeat.seatNumber || selectedSeat.column + 1,
            layoutX: selectedSeat.layoutX !== undefined ? selectedSeat.layoutX : null,
            layoutY: selectedSeat.layoutY !== undefined ? selectedSeat.layoutY : null,
            color: selectedSeat.color || "",
            status: toFirestoreStatus(selectedSeat.status),
            priceId: selectedSeat.type,
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
          },
          { merge: true }
        );

      setSuccess(`${selectedSeat.name} saved.`);
    } catch (err) {
      setError(`Error saving seat: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllSeats = async () => {
    try {
      setSaving(true);
      clearMessages();

      const batch = fires.batch();

      seats.forEach((seat) => {
        const seatRef = fires.collection("events").doc(eventId).collection("seats").doc(seat.id);
        batch.set(
          seatRef,
          {
            id: seat.id,
            name: seat.name,
            displayTitle: seat.name,
            rowLabel: seat.rowLabel || "",
            section: seat.section || "",
            x: seat.row,
            y: seat.column,
            seatNumber: seat.seatNumber || seat.column + 1,
            layoutX: seat.layoutX !== undefined ? seat.layoutX : null,
            layoutY: seat.layoutY !== undefined ? seat.layoutY : null,
            color: seat.color || "",
            status: toFirestoreStatus(seat.status),
            priceId: seat.type,
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
          },
          { merge: true }
        );
      });

      await batch.commit();
      setSuccess(`Saved ${seats.length} seats.`);
    } catch (err) {
      setError(`Error saving seats: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeat = async () => {
    if (!selectedSeat) return;

    const shouldDelete = window.confirm(`Delete ${selectedSeat.name}? This cannot be undone.`);
    if (!shouldDelete) return;

    try {
      setSaving(true);
      clearMessages();

      await fires.collection("events").doc(eventId).collection("seats").doc(selectedSeat.id).delete();
      const nextSeats = seats.filter((seat) => seat.id !== selectedSeat.id);
      setSeats(nextSeats);
      setSelectedSeatId(nextSeats[0]?.id || "");
      setSuccess(`${selectedSeat.name} deleted.`);
    } catch (err) {
      setError(`Error deleting seat: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const selectedSeatPosition = selectedSeat ? getSeatCanvasPosition(selectedSeat) : null;
  const mapCardStyle = planFullscreen
    ? {
        border: "0",
        borderRadius: 0,
        position: "fixed",
        inset: 0,
        zIndex: 2050,
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
      }
    : { border: "1px solid #dbe3ee", borderRadius: "8px" };
  const mapCanvasHeight = planFullscreen ? "100%" : "680px";

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <div className="mt-3">Loading seat map...</div>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Seat Selection V2 - {eventData?.title || eventData?.eventTitle || "EventGo"}</title>
      </Helmet>

      <div style={{ background: "#eef2f7", minHeight: "calc(100vh - 60px)", padding: "24px 0" }}>
        <Container fluid style={{ maxWidth: "1480px" }}>
          <Row className="align-items-center g-3 mb-3">
            <Col>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Button variant="outline-dark" size="sm" onClick={() => navigate(-1)}>
                  <FaArrowLeft /> Back
                </Button>
                <div>
                  <h2 style={{ margin: 0, fontSize: "26px", fontWeight: 800 }}>
                    <FaChair style={{ marginRight: "10px" }} />
                    Seat Selection V2
                  </h2>
                  <div style={{ color: "#64748b", fontSize: "14px" }}>
                    {eventData?.title || eventData?.eventTitle || eventId}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                onClick={loadEventData}
                disabled={saving}
                style={{ marginRight: "8px" }}
              >
                <FaSyncAlt /> Reload
              </Button>
              <Button variant="primary" onClick={handleSaveAllSeats} disabled={saving || seats.length === 0}>
                {saving ? <Spinner animation="border" size="sm" /> : <FaSave />} Save all seats
              </Button>
            </Col>
          </Row>

          {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

          <Row className="g-3 mb-3">
            {statusOptions.map((status) => (
              <Col xs={6} md={3} key={status.value}>
                <Card style={{ border: "1px solid #dbe3ee", borderRadius: "8px" }}>
                  <Card.Body style={{ padding: "14px 16px" }}>
                    <div style={{ color: "#64748b", fontSize: "12px", textTransform: "uppercase" }}>
                      {status.label}
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 800, color: status.color }}>
                      {seatCounts[status.value] || 0}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="g-3">
            <Col lg={planFullscreen ? 12 : 9}>
              <Card style={mapCardStyle}>
                <Card.Header style={{ background: "#ffffff" }}>
                  <Row className="g-2 align-items-end">
                    <Col md={3}>
                      <Form.Label>Status</Form.Label>
                      <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="all">All statuses</option>
                        {statusOptions.map((status) => (
                          <option value={status.value} key={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label>Ticket type</Form.Label>
                      <Form.Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                        <option value="all">All ticket types</option>
                        {seatTypes.map((type) => (
                          <option value={type.id} key={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Rows</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="26"
                        value={rowsToCreate}
                        onChange={(event) => setRowsToCreate(event.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Columns</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="100"
                        value={columnsToCreate}
                        onChange={(event) => setColumnsToCreate(event.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button variant="outline-dark" onClick={handleCreateDraftMap} style={{ width: "100%" }}>
                        <FaPlus /> Draft
                      </Button>
                    </Col>
                  </Row>
                  <Row className="g-2 align-items-end mt-2">
                    <Col md={5}>
                      <Form.Label>Layout preset</Form.Label>
                      <Form.Select
                        value={selectedPreset}
                        onChange={(event) => setSelectedPreset(event.target.value)}
                      >
                        {availableLayoutPresets.map((preset) => (
                          <option value={preset.value} key={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Button variant="outline-primary" onClick={handleCreatePresetMap} style={{ width: "100%" }}>
                        <FaPlus /> Create preset
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Form.Check
                        type="switch"
                        id="layout-edit-mode"
                        label="Drag seats"
                        checked={layoutEditMode}
                        onChange={(event) => {
                          setLayoutEditMode(event.target.checked);
                          if (event.target.checked) setBulkSelectMode(false);
                        }}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Check
                        type="switch"
                        id="bulk-select-mode"
                        label="Box select"
                        checked={bulkSelectMode}
                        onChange={(event) => handleBulkSelectModeChange(event.target.checked)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Check
                        type="checkbox"
                        id="snap-to-grid"
                        label="Snap 10px"
                        checked={snapToGrid}
                        onChange={(event) => setSnapToGrid(event.target.checked)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        variant={planFullscreen ? "dark" : "outline-dark"}
                        onClick={() => setPlanFullscreen((current) => !current)}
                        style={{ width: "100%" }}
                      >
                        {planFullscreen ? <FaCompress /> : <FaExpand />}{" "}
                        {planFullscreen ? "Exit" : "Full screen"}
                      </Button>
                    </Col>
                    {planFullscreen && (
                      <Col md={2}>
                        <Button
                          variant="primary"
                          onClick={handleSaveAllSeats}
                          disabled={saving || seats.length === 0}
                          style={{ width: "100%" }}
                        >
                          {saving ? <Spinner animation="border" size="sm" /> : <FaSave />} Save all
                        </Button>
                      </Col>
                    )}
                  </Row>
                  {(bulkSelectMode || bulkSelectedSeatIds.length > 0) && (
                    <Row className="g-2 align-items-center mt-2">
                      <Col md={4}>
                        <div style={{ color: "#475569", fontSize: "14px", fontWeight: 700 }}>
                          <FaMousePointer style={{ marginRight: "6px" }} />
                          {bulkSelectedSeatIds.length} seats selected
                        </div>
                      </Col>
                      <Col md={2}>
                        <Button
                          variant="warning"
                          onClick={() => handleBulkStatusChange("reserved")}
                          disabled={bulkSelectedSeatIds.length === 0}
                          style={{ width: "100%" }}
                        >
                          Reserve
                        </Button>
                      </Col>
                      <Col md={2}>
                        <Button
                          variant="outline-success"
                          onClick={() => handleBulkStatusChange("pending")}
                          disabled={bulkSelectedSeatIds.length === 0}
                          style={{ width: "100%" }}
                        >
                          Pending
                        </Button>
                      </Col>
                      <Col md={2}>
                        <Button
                          variant="outline-secondary"
                          onClick={handleClearBulkSelection}
                          disabled={bulkSelectedSeatIds.length === 0}
                          style={{ width: "100%" }}
                        >
                          Clear
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Card.Header>
                <Card.Body style={{ padding: 0, flex: 1, minHeight: 0, position: "relative" }}>
                  <div
                    ref={canvasRef}
                    style={{
                      height: mapCanvasHeight,
                      minHeight: planFullscreen ? "0" : "520px",
                      width: "100%",
                      backgroundColor: "#ffffff",
                      backgroundImage:
                        "linear-gradient(#edf2f7 1px, transparent 1px), linear-gradient(90deg, #edf2f7 1px, transparent 1px)",
                      backgroundSize: "80px 80px",
                      overflow: "hidden",
                    }}
                  />
                  {selectionBox && (
                    <div
                      style={{
                        position: "absolute",
                        left: selectionBox.left,
                        top: selectionBox.top,
                        width: selectionBox.width,
                        height: selectionBox.height,
                        border: "2px solid #2563eb",
                        background: "rgba(37, 99, 235, 0.14)",
                        pointerEvents: "none",
                        zIndex: 2,
                      }}
                    />
                  )}
                  {planFullscreen && selectedSeat && (
                    <div
                      style={{
                        position: "absolute",
                        right: "18px",
                        top: "18px",
                        width: "280px",
                        background: "rgba(255, 255, 255, 0.94)",
                        border: "1px solid #dbe3ee",
                        borderRadius: "8px",
                        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
                        padding: "14px",
                      }}
                    >
                      <div style={{ fontWeight: 800, fontSize: "18px", marginBottom: "8px" }}>
                        {selectedSeat.name}
                      </div>
                      <Row className="g-2 mb-2">
                        <Col>
                          <Form.Label>X</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={selectedSeatPosition?.x ?? 0}
                            onChange={(event) => handlePositionInputChange("x", event.target.value)}
                          />
                        </Col>
                        <Col>
                          <Form.Label>Y</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={selectedSeatPosition?.y ?? 0}
                            onChange={(event) => handlePositionInputChange("y", event.target.value)}
                          />
                        </Col>
                      </Row>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                        <Button size="sm" variant="outline-secondary" onClick={() => handleMoveSelectedSeat(-10, 0)}>
                          Left
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => handleMoveSelectedSeat(10, 0)}>
                          Right
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => handleMoveSelectedSeat(0, -10)}>
                          Up
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => handleMoveSelectedSeat(0, 10)}>
                          Down
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3}>
              <Card style={{ border: "1px solid #dbe3ee", borderRadius: "8px", marginBottom: "12px" }}>
                <Card.Header style={{ background: "#ffffff", fontWeight: 800 }}>Smart select</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label>Rows</Form.Label>
                    <Form.Control
                      value={smartRows}
                      placeholder="A-C,J,L"
                      onChange={(event) => setSmartRows(event.target.value)}
                    />
                  </Form.Group>

                  <Row className="g-2 mb-2">
                    <Col>
                      <Form.Label>From</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={smartSeatFrom}
                        onChange={(event) => setSmartSeatFrom(event.target.value)}
                      />
                    </Col>
                    <Col>
                      <Form.Label>To</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={smartSeatTo}
                        onChange={(event) => setSmartSeatTo(event.target.value)}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label>Section</Form.Label>
                    <Form.Select value={smartSection} onChange={(event) => setSmartSection(event.target.value)}>
                      <option value="all">All sections</option>
                      {sectionOptions.map((section) => (
                        <option value={section} key={section}>
                          {section}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Row className="g-2 mb-3">
                    <Col>
                      <Form.Label>Status</Form.Label>
                      <Form.Select value={smartStatus} onChange={(event) => setSmartStatus(event.target.value)}>
                        <option value="all">All</option>
                        {statusOptions.map((status) => (
                          <option value={status.value} key={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <Form.Label>Type</Form.Label>
                      <Form.Select value={smartType} onChange={(event) => setSmartType(event.target.value)}>
                        <option value="all">All</option>
                        {seatTypes.map((type) => (
                          <option value={type.id} key={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <Button variant="outline-dark" onClick={() => handleSmartSelect(false)}>
                      Select
                    </Button>
                    <Button variant="outline-secondary" onClick={() => handleSmartSelect(true)}>
                      Add
                    </Button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" }}>
                    <Button variant="outline-dark" onClick={handleSelectRightHalfSeats}>
                      Select right half
                    </Button>
                    <Button variant="warning" onClick={handleReserveRightHalfSeats}>
                      Reserve right half
                    </Button>
                  </div>
                  <Button
                    variant="dark"
                    onClick={handleApplyLeftOpenHoldPattern}
                    style={{ width: "100%", marginTop: "8px" }}
                  >
                    Apply left tiers, lock right
                  </Button>
                </Card.Body>
              </Card>

              <Card style={{ border: "1px solid #dbe3ee", borderRadius: "8px", marginBottom: "12px" }}>
                <Card.Header style={{ background: "#ffffff", fontWeight: 800 }}>Ticket type colors</Card.Header>
                <Card.Body>
                  <div style={{ display: "grid", gap: "10px" }}>
                    {seatTypes.map((type) => (
                      <div
                        key={type.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "42px 1fr 86px",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <Form.Control
                          type="color"
                          value={type.color || fallbackSeatTypes[0].color}
                          onChange={(event) => handleTicketTypeColorChange(type.id, event.target.value)}
                          title={`Set ${type.name} color`}
                          style={{ height: "36px", padding: "4px" }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {type.name}
                          </div>
                          <div style={{ color: "#64748b", fontSize: "12px" }}>
                            {type.price ? `$${type.price}` : "Free"}
                          </div>
                        </div>
                        <Form.Control
                          size="sm"
                          value={type.color || fallbackSeatTypes[0].color}
                          onChange={(event) => handleTicketTypeColorChange(type.id, event.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline-primary"
                    onClick={handleSaveTicketTypeColors}
                    disabled={saving || seatTypes.length === 0}
                    style={{ width: "100%", marginTop: "12px" }}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : <FaSave />} Save colors
                  </Button>
                </Card.Body>
              </Card>

              <Card style={{ border: "1px solid #dbe3ee", borderRadius: "8px" }}>
                <Card.Header style={{ background: "#ffffff", fontWeight: 800 }}>Selected seat</Card.Header>
                <Card.Body>
                  {!selectedSeat ? (
                    <div style={{ color: "#64748b" }}>Select a seat on the map to edit it.</div>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                        <div>
                          <div style={{ fontSize: "28px", fontWeight: 800 }}>{selectedSeat.name}</div>
                          <div style={{ color: "#64748b", fontSize: "13px" }}>
                            Row {selectedSeat.row + 1}, Column {selectedSeat.column + 1}
                          </div>
                          {selectedSeat.section && (
                            <div style={{ color: "#64748b", fontSize: "13px" }}>{selectedSeat.section}</div>
                          )}
                        </div>
                        <Badge bg="secondary" style={{ height: "24px" }}>
                          {selectedSeat.id}
                        </Badge>
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={selectedSeat.name}
                          onChange={(event) => updateSeat(selectedSeat.id, { name: event.target.value })}
                        />
                      </Form.Group>

                      <Row className="g-2 mb-3">
                        <Col>
                          <Form.Label>X</Form.Label>
                          <Form.Control
                            type="number"
                            value={selectedSeatPosition?.x ?? 0}
                            onChange={(event) => handlePositionInputChange("x", event.target.value)}
                          />
                        </Col>
                        <Col>
                          <Form.Label>Y</Form.Label>
                          <Form.Control
                            type="number"
                            value={selectedSeatPosition?.y ?? 0}
                            onChange={(event) => handlePositionInputChange("y", event.target.value)}
                          />
                        </Col>
                      </Row>

                      <div style={{ marginBottom: "16px" }}>
                        <Form.Label>
                          <FaArrowsAlt /> Nudge location
                        </Form.Label>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 44px)",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                        >
                          <span />
                          <Button variant="outline-secondary" size="sm" onClick={() => handleMoveSelectedSeat(0, -10)}>
                            Up
                          </Button>
                          <span />
                          <Button variant="outline-secondary" size="sm" onClick={() => handleMoveSelectedSeat(-10, 0)}>
                            Left
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => handleMoveSelectedSeat(0, 10)}>
                            Down
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => handleMoveSelectedSeat(10, 0)}>
                            Right
                          </Button>
                        </div>
                      </div>

                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={selectedSeat.status}
                          onChange={(event) => updateSeat(selectedSeat.id, { status: event.target.value })}
                        >
                          {statusOptions.map((status) => (
                            <option value={status.value} key={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Ticket type</Form.Label>
                        <Form.Select
                          value={selectedSeat.type}
                          onChange={(event) =>
                            updateSeat(selectedSeat.id, {
                              type: event.target.value,
                              priceId: event.target.value,
                            })
                          }
                        >
                          {seatTypes.map((type) => (
                            <option value={type.id} key={type.id}>
                              {type.name} {type.price ? `($${type.price})` : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <div style={{ display: "grid", gap: "8px" }}>
                        <Button variant="primary" onClick={handleSaveSeat} disabled={saving}>
                          {saving ? <Spinner animation="border" size="sm" /> : <FaSave />} Save selected seat
                        </Button>
                        <Button variant="outline-danger" onClick={handleDeleteSeat} disabled={saving}>
                          <FaTrash /> Delete selected seat
                        </Button>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}
